import { format, isValid } from 'date-fns';
import { useCallback, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { toast } from 'sonner';

import type { BirthdayStatusFilter } from '~entities/student/api/reporting-birthdays';
import { reportingInactiveStudentsAPI } from '~entities/student/api/reporting-inactive-students';
import { reportingStudentsByAgeAPI } from '~entities/student/api/reporting-students-by-age';
import { months } from '~entities/student/constants/monthsList';
import { downloadConsolidatedStudioDatabaseReport } from '~entities/reporting/api/consolidated-studio-database-report';

import {
  ALL_MONTHS,
  type MonthSelectorValue,
  reports,
  ReportType,
} from '~pages/payments/payment-reports/report-config';

import { KEYS } from '~shared/api/query-keys';

import { revenueByClassReportHasRows } from './use-report-download.helpers';
import type { useReportQueries } from './use-report-queries';

function downloadCSV(csvText: string, filename: string) {
  const blob = new Blob([csvText], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

function sanitizeReportFilename(segment: string) {
  return segment
    .replace(/[/\\?%*:|"<>#]+/g, '-')
    .trim()
    .slice(0, 72);
}

function csvTextFromDashboardResponse(data: unknown) {
  return typeof data === 'string' && data.trim().length > 0
    ? data
    : 'No data available';
}

function classGroupsReportHasEnrollmentRows(csvText: string): boolean {
  const totalMatch = csvText.match(/Total Enrollments - (\d+)/);
  if (totalMatch) {
    return Number(totalMatch[1]) > 0;
  }

  const lines = csvText
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);
  const headerRowIndex = lines.findIndex(
    (line) =>
      line.includes('Student Name') &&
      line.includes('Parent Name') &&
      line.includes('Class Name'),
  );
  return headerRowIndex >= 0 && headerRowIndex < lines.length - 1;
}

function sessionStudentsEnrollmentsReportHasRows(csvText: string): boolean {
  const studentsMatch = csvText.match(/Students - (\d+)/);
  if (studentsMatch) {
    return Number(studentsMatch[1]) > 0;
  }

  const lines = csvText
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);
  const headerRowIndex = lines.findIndex(
    (line) =>
      line.includes('Student Name') &&
      line.includes('All Session Classes') &&
      line.includes('Matching Classes'),
  );
  return headerRowIndex >= 0 && headerRowIndex < lines.length - 1;
}

interface UseReportDownloadParams {
  queries: ReturnType<typeof useReportQueries>;
  dateRanges: Record<string, DateRange | undefined>;
  selectedSession: { label: string; value: string } | null;
  selectedClass: { label: string; value: string } | null;
  selectedGroup: { label: string; value: string } | null;
  selectedLocation: { label: string | null; value: string | null } | null;
  daysPastDue: number | '';
  stripePaymentIntents: number | string;
  transactionCodesLookbackDays: number | string;
  dueDate: Date | undefined;
  selectedMonth: MonthSelectorValue;
  birthdayStatus: BirthdayStatusFilter;
  classKeywords: string;
  ageAsOfDate: Date | undefined;
  studentMinAge: number | '';
  studentMaxAge: number | '';
}

export function useReportDownload({
  queries,
  dateRanges,
  selectedSession,
  selectedClass,
  selectedGroup,
  selectedLocation,
  daysPastDue,
  stripePaymentIntents,
  transactionCodesLookbackDays,
  dueDate,
  selectedMonth,
  birthdayStatus,
  classKeywords,
  ageAsOfDate,
  studentMinAge,
  studentMaxAge,
}: UseReportDownloadParams) {
  const [loadingReportId, setLoadingReportId] = useState<ReportType | null>(
    null,
  );

  const downloadReport = useCallback(
    async (reportName: string, reportId?: ReportType) => {
      const id = reportId ?? reports.find((r) => r.name === reportName)?.id;
      if (!id) return;

      setLoadingReportId(id);

      try {
        let csvText = '';

        switch (id) {
          case ReportType.ParentOutstandingBalances: {
            const { data } =
              await queries.parentOutstandingBalancesQuery.refetch();
            csvText =
              typeof data === 'string' && data ? data : 'No data available';
            downloadCSV(csvText, 'parent-outstanding-balances.csv');
            break;
          }

          case ReportType.OutstandingAndOverdue: {
            const { data } =
              await queries.outstandingAndOverdueQuery.refetch();
            csvText =
              typeof data === 'string' && data ? data : 'No data available';
            downloadCSV(csvText, 'outstanding-and-overdue.csv');
            break;
          }

          case ReportType.RevenueBySession: {
            if (!selectedSession) throw new Error('Select a session');
            const { data } = await queries.paymentsBySessionQuery.refetch();
            csvText = data ?? '';
            downloadCSV(
              csvText,
              `revenue-by-session-${selectedSession.label}.csv`,
            );
            break;
          }

          case ReportType.RevenueByClass: {
            if (!selectedSession || !selectedClass)
              throw new Error('Select session and class');
            const { data } = await queries.paymentsByClassQuery.refetch();
            csvText = data ?? '';
            if (!revenueByClassReportHasRows(csvText)) {
              toast.info('No paid revenue found for the selected class');
              break;
            }
            downloadCSV(csvText, `revenue-by-class-${selectedClass.label}.csv`);
            break;
          }

          case ReportType.RevenueOverPeriod: {
            if (!dateRanges[id]?.from || !dateRanges[id].to)
              throw new Error('Select date range');
            const { data } = await queries.revenueOverTimePeriodQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, 'revenue-over-period.csv');
            break;
          }

          case ReportType.TopSpendingParents: {
            const { data } = await queries.topSpendingParentsQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, 'top-spending-parents.csv');
            break;
          }

          case ReportType.ScheduledUpcomingPayments: {
            if (!dateRanges[id]?.from || !dateRanges[id].to)
              throw new Error('Select date range');
            const { data } =
              await queries.scheduledUpcomingPaymentsQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, 'scheduled-upcoming-payments.csv');
            break;
          }

          case ReportType.IssuedRefunds: {
            if (!dateRanges[id]?.from || !dateRanges[id].to)
              throw new Error('Select date range');
            const { data } = await queries.issuedRefundsQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, 'issued-refunds.csv');
            break;
          }

          case ReportType.FailedPayments: {
            if (!dateRanges[id]?.from || !dateRanges[id].to)
              throw new Error('Select date range');
            const { data } = await queries.failedPaymentsQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, 'failed-payments.csv');
            break;
          }

          case ReportType.DaysPastDue: {
            if (!daysPastDue || typeof daysPastDue !== 'number')
              throw new Error('Enter days past due');
            const { data } =
              await queries.daysPastDueOverduePaymentsQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, `days-past-due-${daysPastDue}.csv`);
            break;
          }

          case ReportType.EnrollmentsBySession: {
            if (!selectedSession) throw new Error('Select a session');
            const { data } = await queries.enrollmentBySessionQuery.refetch();
            csvText = data ?? '';
            downloadCSV(
              csvText,
              `enrollments-by-session-${selectedSession.label}.csv`,
            );
            break;
          }

          case ReportType.PaymentsBySession: {
            if (!selectedSession) throw new Error('Select a session');
            const { data } =
              await queries.sessionPaymentsDetailsQuery.refetch();
            csvText = data ?? '';
            downloadCSV(
              csvText,
              `payments-by-session-${selectedSession.label}.csv`,
            );
            break;
          }

          case ReportType.ActiveStudentsClassCount: {
            const { data } =
              await queries.activeStudentsClassCountQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, 'active-students-class-count.csv');
            break;
          }

          case ReportType.ClassEnrollmentsWithStudentDetails: {
            if (!dateRanges[id]?.from || !dateRanges[id].to)
              throw new Error('Select date range');
            const { data } = await queries.enrollsByClassQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, 'class-enrollments-with-student-details.csv');
            break;
          }

          case ReportType.NewVsReturningStudents: {
            const selectedRange = dateRanges[id];
            if (
              !selectedSession?.value &&
              (!selectedRange?.from || !selectedRange.to)
            ) {
              throw new Error('Select a session or date range');
            }
            const { data } = await queries.newVsReturningStudentsQuery.refetch();
            csvText = data ?? '';
            const startStr = selectedRange?.from
              ? format(selectedRange.from, 'yyyy-MM-dd')
              : '';
            const endStr = selectedRange?.to
              ? format(selectedRange.to, 'yyyy-MM-dd')
              : '';
            const fileSegment = selectedSession?.label
              ? sanitizeReportFilename(selectedSession.label)
              : startStr && endStr
                ? `${startStr}-to-${endStr}`
                : 'report';
            downloadCSV(
              csvText,
              `new-vs-returning-students-${fileSegment}.csv`,
            );
            break;
          }

          case ReportType.ClassAndEventEnrollments: {
            if (!dateRanges[id]?.from || !dateRanges[id].to)
              throw new Error('Select date range');
            const { data } =
              await queries.enrollsByClassAndEventQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, 'class-enrollment-by-date.csv');
            break;
          }

          case ReportType.ClassGroupsReport: {
            if (!selectedSession?.value || !selectedGroup?.value) {
              throw new Error('Select session and group');
            }
            const { data } = await queries.groupEnrollmentsReportQuery.refetch();
            csvText = data ?? '';
            if (!classGroupsReportHasEnrollmentRows(csvText)) {
              toast.info('No enrolled students found for the selected group');
              break;
            }
            downloadCSV(
              csvText,
              `class-groups-report-${sanitizeReportFilename(selectedGroup.label)}.csv`,
            );
            break;
          }

          case ReportType.SessionStudentsEnrollments: {
            if (!selectedSession?.value) {
              throw new Error('Select a session');
            }
            const { data } =
              await queries.sessionStudentsEnrollmentsQuery.refetch();
            csvText = data ?? '';
            if (!sessionStudentsEnrollmentsReportHasRows(csvText)) {
              toast.info('No enrolled students found for the selected filters');
              break;
            }
            downloadCSV(
              csvText,
              `session-students-enrollments-${sanitizeReportFilename(selectedSession.label)}.csv`,
            );
            break;
          }

          case ReportType.TransactionDetails: {
            if (!dateRanges[id]?.from || !dateRanges[id].to)
              throw new Error('Select date range');
            const { data } = await queries.transactionDetailsQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, 'detailed-transaction-report.csv');
            break;
          }

          case ReportType.StripePaymentIntentsReport: {
            if (
              !stripePaymentIntents ||
              typeof stripePaymentIntents !== 'number'
            )
              throw new Error('Enter days');
            const { data } =
              await queries.paymentProviderStatementQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, 'stripe-payment-intents-report.csv');
            break;
          }

          case ReportType.TransactionCodesReport: {
            if (
              !transactionCodesLookbackDays ||
              typeof transactionCodesLookbackDays !== 'number'
            )
              throw new Error('Enter days');
            const { data } =
              await queries.transactionCodesReportQuery.refetch();
            csvText = data ?? '';
            downloadCSV(csvText, 'transaction-codes-report.csv');
            break;
          }

          case ReportType.PaymentsByDueDate: {
            if (!dueDate) throw new Error('Select a due date');
            const { data } = await queries.paymentsByDueDateQuery.refetch();
            csvText = data ?? '';
            downloadCSV(
              csvText,
              `payment-due-date-report-${format(dueDate, 'yyyy-MM-dd')}.csv`,
            );
            break;
          }

          case ReportType.RevenueByLocation: {
            if (!selectedLocation?.value) throw new Error('Select a location');
            const { data } = await queries.revenueByLocationQuery.refetch();
            csvText = data ?? '';
            downloadCSV(
              csvText,
              `revenue-by-location-${selectedLocation.label ?? 'location'}.csv`,
            );
            break;
          }

          case ReportType.TrialClassReport: {
            if (!dateRanges[id]?.from || !dateRanges[id].to)
              throw new Error('Select date range');
            const { data } = await queries.trialClassReportQuery.refetch();
            csvText = data ?? '';
            const startStr = format(dateRanges[id].from, 'yyyy-MM-dd');
            const endStr = format(dateRanges[id].to, 'yyyy-MM-dd');
            downloadCSV(
              csvText,
              `trial-class-report-${startStr}-to-${endStr}.csv`,
            );
            break;
          }

          case ReportType.DroppedClassReport: {
            const dateRange = dateRanges[id];
            if (!dateRange?.from || !dateRange.to)
              throw new Error('Select date range');
            const { data: droppedData } =
              await queries.droppedClassReportQuery.refetch();
            csvText = droppedData ?? '';
            const dropStart = format(dateRange.from, 'yyyy-MM-dd');
            const dropEnd = format(dateRange.to, 'yyyy-MM-dd');
            downloadCSV(
              csvText,
              `dropped-class-report-${dropStart}-to-${dropEnd}.csv`,
            );
            break;
          }

          case ReportType.FullyBookedClassesBySession: {
            if (!selectedSession) throw new Error('Select a session');
            const { data } =
              await queries.fullyBookedClassesBySessionQuery.refetch();
            csvText = csvTextFromDashboardResponse(data);
            downloadCSV(
              csvText,
              `fully-booked-classes-${sanitizeReportFilename(selectedSession.label)}.csv`,
            );
            break;
          }

          case ReportType.ClassGenreReport: {
            if (!selectedSession) throw new Error('Select a session');
            const { data } = await queries.classGenreReportQuery.refetch();
            csvText = data ?? '';
            downloadCSV(
              csvText,
              `class-genre-report-${sanitizeReportFilename(selectedSession.label)}.csv`,
            );
            break;
          }

          case ReportType.BirthdayReport: {
            const { data } = await queries.birthdayReportQuery.refetch();
            csvText = data ?? '';
            const scopeLabel =
              selectedMonth === ALL_MONTHS
                ? 'all-months'
                : (months[parseInt(selectedMonth) - 1]?.label ?? selectedMonth);
            downloadCSV(
              csvText,
              `student-birthday-report-${scopeLabel}-${birthdayStatus}.csv`,
            );
            break;
          }

          case ReportType.StudentsByAge: {
            // Direct call with click-time params — a param-keyed refetch can
            // resolve empty if another report's dialog resets the filters
            // mid-flight.
            csvText = await reportingStudentsByAgeAPI({
              status: birthdayStatus,
              ageAsOf:
                ageAsOfDate && isValid(ageAsOfDate)
                  ? format(ageAsOfDate, 'yyyy-MM-dd')
                  : undefined,
              minAge: studentMinAge === '' ? undefined : studentMinAge,
              maxAge: studentMaxAge === '' ? undefined : studentMaxAge,
              format: KEYS.EXPORT_FORMAT.CSV,
            });
            downloadCSV(csvText, 'students-by-age.csv');
            break;
          }

          case ReportType.InactiveStudents: {
            csvText = await reportingInactiveStudentsAPI({
              ageAsOf:
                ageAsOfDate && isValid(ageAsOfDate)
                  ? format(ageAsOfDate, 'yyyy-MM-dd')
                  : undefined,
              minAge: studentMinAge === '' ? undefined : studentMinAge,
              maxAge: studentMaxAge === '' ? undefined : studentMaxAge,
              format: KEYS.EXPORT_FORMAT.CSV,
            });
            downloadCSV(csvText, 'inactive-students.csv');
            break;
          }

          case ReportType.ConsolidatedStudioDatabase: {
            await downloadConsolidatedStudioDatabaseReport();
            toast.success('Consolidated report downloaded successfully');
            break;
          }

          default:
            break;
        }
      } catch (err) {
        toast.error('Failed to download report', {
          description: (err as Error).message,
        });
      } finally {
        setLoadingReportId(null);
      }
    },
    [
      queries,
      dateRanges,
      selectedSession,
      selectedClass,
      selectedGroup,
      selectedLocation,
      daysPastDue,
      stripePaymentIntents,
      transactionCodesLookbackDays,
      dueDate,
      selectedMonth,
      birthdayStatus,
      classKeywords,
      ageAsOfDate,
      studentMinAge,
      studentMaxAge,
    ],
  );

  return {
    downloadReport,
    loadingReportId,
  };
}
